import { Box, Button, FormControl, FormLabel, Link, Typography, RadioGroup, Radio, FormControlLabel, TextField, CircularProgress } from '@mui/material';
import { useMe, useWorkspaceResources, useWorkplaces } from 'core/hooks/broadcast';
import { useDocumentContext } from 'module/documents/context';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useEmployeeFormData } from 'module/documents/hooks';
import { FormGroupSection } from 'module/documents/components/components';

interface FormErrors {
	firstName: boolean;
	lastName: boolean;
	email: boolean;
	existingEmployee: boolean;
}

interface WorkspaceUserAutocompleteProps {
	setdisableTabsForNewEmployee: React.Dispatch<React.SetStateAction<boolean>>;
	setActiveTabIndex: React.Dispatch<React.SetStateAction<number>>;
	formErrors: FormErrors;
}

  export const WorkspaceUserAutocomplete: FC<WorkspaceUserAutocompleteProps> = ({ setdisableTabsForNewEmployee, formErrors }) => {
	const { data: usersData } = useWorkspaceResources();
	const { resetDocumentContext, setTranslatorSignatureFile, selectedEmployee, setSelectedEmployee, formik, newEmployee, setNewEmployee, newEmployeeCreated,  employeeState, setEmployeeState } = useDocumentContext();
	const [isChangingUser, setIsChangingUser] = useState(false);
	const [enableSelectEmployee, setenableSelectEmployee] = useState(false);
	const [disableUserForm, setDisableUserForm] = useState(false);
	const { isLoading } = useEmployeeFormData();
	const { data: usersWorkplaces } = useWorkplaces();

	const handleUserChange = (selectedOption: any) => {
		resetDocumentContext('dont_reset');
		setIsChangingUser(true);

		if (!selectedOption) {
			setSelectedEmployee(null);
			return;
		}
		setSelectedEmployee(usersData[selectedOption.index] || null);

		setTimeout(() => {
			setIsChangingUser(false);
		}, 2000);
	};

	const filteredOptions = useMemo(() => {
		if (!usersData) {
			return [];
		}

		return usersData.map((user, i) => {
			return {
				index: i,
				value: user.id,
				email: user.email,
				label: `${user.firstName} ${user.lastName} (${user.email})`,
			};
		});
	}, [usersData]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setNewEmployee((prev) => ({ ...prev, [name]: value }));
	};

	const handleSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
	  	const newValue = event.target.value;
	  	setEmployeeState(newValue);
		if (newValue === "existing_employee") {
			setdisableTabsForNewEmployee(false);
			setenableSelectEmployee(true);
			setDisableUserForm(true);
			//SET FORM VALUES
			formik.setFieldValue('employer_business_name', usersWorkplaces?.items?.[0]?.name);
			formik.setFieldValue('employer_city', usersWorkplaces?.items?.[0]?.city);
			formik.setFieldValue('employer_ein', usersWorkplaces?.items?.[0]?.identifier); 
			formik.setFieldValue('employer_business_address', (usersWorkplaces?.items?.[0]?.street_address + ", " + usersWorkplaces?.items?.[0]?.state + ", " + usersWorkplaces?.items?.[0]?.city + ", " + usersWorkplaces?.items?.[0]?.country));
			formik.setFieldValue('employer_state', usersWorkplaces?.items?.[0]?.state);
			formik.setFieldValue('employer_zip_code', usersWorkplaces?.items?.[0]?.postcode);
			formik.setFieldValue('employer_title', usersWorkplaces?.items?.[0]?.division?.name);
			setSelectedEmployee((prev) => ({ ...prev }));
		} else {
			resetDocumentContext('dont_reset');
			setDisableUserForm(false);
			setdisableTabsForNewEmployee(true);
			setenableSelectEmployee(false);
			if (newEmployeeCreated) {
				setdisableTabsForNewEmployee(false);
			}
		}
	};

	return (
		<>
			<h5 style={{ fontSize: "1.165rem" /* 18px */, marginBottom: "12px" }}>Select type of onboarding to begin.</h5>

			<FormGroupSection title=""
				isEmployer={true}
				marginTop="14px"
				marginBottom="12px">
				<RadioGroup
					aria-label="document-options"
					name="document-options-group"
					row
					value={employeeState}
					onChange={handleSelection}
				>
					<FormControlLabel value="new_employee" control={<Radio />} label="New Employee" />
					<FormControlLabel value="re_hiring_employee" control={<Radio />} label="Re-hire Employee" />
					<FormControlLabel value="existing_employee" control={<Radio />} label="Existing Employee" />
				</RadioGroup>
				<FormControl component="fieldset" fullWidth>
					{(employeeState === "existing_employee" && 
						<>
							<FormLabel
								component="legend"
								sx={{ minHeight: '35px', display: 'flex', alignItems: 'center', gap: '10px' }}
							>
								Select Employee
							{isLoading && <CircularProgress size={20} />}
							</FormLabel>
							<Box mt={1}>
								<Select
									className="basic-single"
									classNamePrefix="select"
									isDisabled={employeeState !== "existing_employee"}
									defaultValue={
										selectedEmployee
											? {
												value: selectedEmployee.id,
												email: selectedEmployee.email,
												label: `${selectedEmployee.firstName} ${selectedEmployee.lastName} (${selectedEmployee.email})`,
											}
											: null
									}
									isLoading={isLoading || isChangingUser}
									onChange={handleUserChange}
									// isClearable={true}
									isSearchable={true}
									name="employees"
									placeholder="Select Employee"
									options={filteredOptions}
									styles={{
										control: (base, state) => ({
											...base,
											borderColor: formErrors.existingEmployee ? 'red' : state.isFocused ? 'blue' : base.borderColor,
											boxShadow: formErrors.existingEmployee ? '0 0 5px red' : 'none',
										}),
										option: (baseStyles, _state) => {
											return baseStyles;
										},
									}}
								/>
								{formErrors.existingEmployee && <Typography color="error">Please select an existing employee</Typography>}
							</Box>
						</>
					)}
					</FormControl>
				{(employeeState === "new_employee" || employeeState === "re_hiring_employee") && (<Box component="form" mt={2}>
					<FormControl component="fieldset">

						<Box sx={{ display: "flex", gap: 2 }}>
							<TextField inputProps={{ maxLength: 50 }} onChange={handleChange} helperText={formErrors.firstName ? "First Name is required" : ""} label="First Name" error={formErrors.firstName} name="firstName" value={newEmployee.firstName} fullWidth />
							<TextField inputProps={{ maxLength: 20 }} onChange={handleChange} label="Middle Name" name="middleName" value={newEmployee.middleName} fullWidth />
							<TextField inputProps={{ maxLength: 50 }} onChange={handleChange} error={formErrors.lastName} helperText={formErrors.lastName ? "Last Name is required" : ""} label="Last Name" name="lastName" value={newEmployee.lastName} fullWidth />
						</Box>
						<TextField inputProps={{ maxLength: 100 }} onChange={handleChange} error={formErrors.email} helperText={formErrors.email ? "Valid email is required" : ""} label="Email Address" name="email" type="email" value={newEmployee.email} fullWidth sx={{ mt: 2 }} />
					</FormControl>
				</Box>)}
			</FormGroupSection>
		</>
	);
};

interface FileUploadProps {
	id?: string;
	label?: string;
	existingFilePath?: Record<any, any>;
	onFileChange?: (file: File) => void;
}

export const FileUploadField: FC<FileUploadProps> = ({ id, existingFilePath, label, onFileChange }) => {
	const [selectedFile, setSelectedFile] = useState<File | null>(null);

	const { selectedEmployee } = useDocumentContext();

	const { data: me } = useMe();

	console.log('existingFilePath:', existingFilePath);

	// const isActiveUserSelected = selectedEmployee.user.id === me.id;
	const isActiveUserSelected = true;

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			setSelectedFile(file);
			onFileChange(file);
		}
	};

	return (
		<Box display="flex" flexDirection="column" alignItems="flex-start" gap={2} mb={2}>
			{label && <Typography variant="h6">{label}</Typography>}

			{existingFilePath && (
				<Link href={process.env.REACT_APP_URL + existingFilePath} target="_blank">
					<img
						src={process.env.REACT_APP_URL + existingFilePath}
						style={{ marginBottom: 20, maxHeight: '200px' }}
					/>
				</Link>
			)}

			<input
				// accept=".jpg,.jpeg,.png,.pdf,.docx"
				accept="image/*"
				style={{ display: 'none' }}
				id={id}
				type="file"
				onChange={handleFileChange}
			/>
			<label htmlFor={id}>
				<Button
					variant="contained"
					component="span"
					startIcon={<CloudUploadIcon />}
					sx={{ marginRight: 2 }}
					disabled={!isActiveUserSelected}
				>
					Choose File
				</Button>

				{selectedFile && (
					<Typography variant="body1">{selectedFile.name}</Typography>
				)}
			</label>
		</Box>
	);
};
